/**
 * Widget Wizard Constants
 * Centralized configuration for the widget creation wizard
 */

// Wizard steps configuration (components added in index.jsx to avoid circular imports)
export const WIZARD_STEP_CONFIG = [
  { id: 1, name: 'dashboard.widgetWizard.steps.vizType' },
  { id: 2, name: 'dashboard.widgetWizard.steps.dataSource' },
  { id: 3, name: 'dashboard.widgetWizard.steps.chartConfig' },
];

// Widget types configuration - excludes treemap, timeseries, and stacked-line
export const WIDGET_TYPES = [
  {
    value: 'metric',
    label: 'KPI',
    description: 'dashboard.widgetWizard.descriptions.metric'
  },
  {
    value: 'pie',
    label: 'Pie Chart',
    description: 'dashboard.widgetWizard.descriptions.pie'
  },
  {
    value: 'donut',
    label: 'Donut Chart',
    description: 'dashboard.widgetWizard.descriptions.donut'
  },
  {
    value: 'bar',
    label: 'Bar Chart',
    description: 'dashboard.widgetWizard.descriptions.bar'
  },
  {
    value: 'grouped-bar',
    label: 'Grouped Bar Chart',
    description: 'dashboard.widgetWizard.descriptions.groupedBar'
  },
  {
    value: 'stacked-bar',
    label: 'Stacked Bar Chart',
    description: 'dashboard.widgetWizard.descriptions.stackedBar'
  },
  {
    value: 'line',
    label: 'Line Chart',
    description: 'dashboard.widgetWizard.descriptions.line'
  },
  {
    value: 'table',
    label: 'Table Chart',
    description: 'dashboard.widgetWizard.descriptions.table'
  },
  {
    value: 'text',
    label: 'Text',
    description: 'dashboard.widgetWizard.descriptions.text'
  },
];

// Time pivot options for Metric Values
export const TIME_PIVOT_OPTIONS = [
  { value: '1m', label: 'dashboard.widgetWizard.timePivot.1m' },
  { value: '5m', label: 'dashboard.widgetWizard.timePivot.5m' },
  { value: '15m', label: 'dashboard.widgetWizard.timePivot.15m' },
  { value: '30m', label: 'dashboard.widgetWizard.timePivot.30m' },
  { value: '1h', label: 'dashboard.widgetWizard.timePivot.1h' },
  { value: '6h', label: 'dashboard.widgetWizard.timePivot.6h' },
  { value: '12h', label: 'dashboard.widgetWizard.timePivot.12h' },
  { value: '1d', label: 'dashboard.widgetWizard.timePivot.1d' },
  { value: '7d', label: 'dashboard.widgetWizard.timePivot.7d' },
  { value: '30d', label: 'dashboard.widgetWizard.timePivot.30d' },
];

// Aggregation functions
export const AGGREGATION_FUNCTIONS = [
  { value: 'mean', label: 'dashboard.widgetWizard.aggregation.mean' },
  { value: 'max', label: 'dashboard.widgetWizard.aggregation.max' },
  { value: 'min', label: 'dashboard.widgetWizard.aggregation.min' },
  { value: 'sum', label: 'dashboard.widgetWizard.aggregation.sum' },
  { value: 'count', label: 'dashboard.widgetWizard.aggregation.count' },
];

// Data source tabs
export const DATA_SOURCE_TABS = [
  { value: 'table_columns', label: 'dashboard.widgetWizard.dataSource.entitySelection' },
  { value: 'custom_query', label: 'dashboard.widgetWizard.dataSource.customQuery' },
];

// Inventory widget configuration options
export const INVENTORY_ROW_TYPES = [
  { value: 'hosts', label: 'dashboard.widgetWizard.inventory.rowType.hosts' },
  { value: 'items', label: 'dashboard.widgetWizard.inventory.rowType.items' },
];

export const INVENTORY_CATEGORY_HANDLING = [
  { value: 'column', label: 'dashboard.widgetWizard.inventory.categoryHandling.column' },
  { value: 'specific', label: 'dashboard.widgetWizard.inventory.categoryHandling.specific' },
];

export const INVENTORY_LAYOUTS_HOSTS = [
  { value: 'long_form', label: 'dashboard.widgetWizard.inventory.layout.longForm' },
  { value: 'split_by_category', label: 'dashboard.widgetWizard.inventory.layout.splitByCategory' },
  { value: 'pivot', label: 'dashboard.widgetWizard.inventory.layout.pivot' },
];

export const INVENTORY_LAYOUTS_ITEMS = [
  { value: 'aggregated', label: 'dashboard.widgetWizard.inventory.layout.aggregated' },
  { value: 'expanded', label: 'dashboard.widgetWizard.inventory.layout.expanded' },
];

export const INVENTORY_SCAN_BASIS = [
  { value: 'latest', label: 'dashboard.widgetWizard.inventory.scanBasis.latest' },
];

// Entity types for metric values
export const ENTITY_TYPES = [
  { value: 'metrics', label: 'dashboard.widgetWizard.entityType.metrics' },
  { value: 'hosts', label: 'dashboard.widgetWizard.entityType.hosts' },
];

// Data source types
export const DATA_SOURCE_TYPES = {
  METRIC_VALUES: 'metric_values',
  TABLE: 'table',
  INVENTORY: 'inventory',
  CUSTOM_QUERY: 'custom_query',
};

/**
 * Tables/views to hide from entity selection dropdown.
 * These are internal tables used only by the backend and don't contain
 * user-facing data suitable for reports.
 * MON-17323
 */
export const HIDDEN_DATA_SOURCES = [
  'hostgroup_hosts',
  'widgets',
  'dashboards',
  'business_service_hosts',
  'business_service_metrics',
];

// Maximum number of data sources for line charts (supports dual Y-axis)
export const MAX_LINE_CHART_DATA_SOURCES = 2;

/**
 * Default widget configuration
 * 
 * Structure:
 * - dataSources: Array of data sources, each with type-specific config
 * - Root level: Only widget-level display options
 * 
 * Each data source type has its own config schema:
 * - table: { selectedColumns, chartBy, groupBy, aggregation, valueColumn, timeColumn, yAxisField }
 * - metric_values: { groupBy, aggregateLiteral, aggregateFunction }
 * - inventory: { rowType, categoryHandling, tableLayout, scanBasis, selectedCategories, pivotColumnLimit }
 * - custom_query: { query }
 */
export const DEFAULT_WIDGET_CONFIG = {
  // Data sources with type-specific configs
  dataSources: [],

  // === Widget-level display options only ===
  displayName: '',             // Custom title/subtitle
  textContent: '',             // For text widgets only
};

/**
 * Default config for each data source type
 */
export const DEFAULT_TABLE_CONFIG = {
  selectedColumns: [],         // For table widget - columns to display
  chartBy: '',                 // Column to GROUP BY (becomes category/name)
  groupBy: '',                 // Secondary GROUP BY (for multi-series charts, or entity column for line charts)
  aggregation: 'count',        // count | sum | avg | min | max | count_distinct
  valueColumn: '',             // Column to aggregate (for sum/avg/etc)
  timeColumn: '',              // Timestamp column (X-axis) for line charts
  yAxisField: '',              // Value column (Y-axis) for line charts
  categoryLimit: '',           // Max categories for pie/bar (rest grouped as "Other")
};

export const DEFAULT_METRIC_VALUES_CONFIG = {
  groupBy: 'metrics',          // "metrics" or "hosts" - what entities to show
  aggregateLiteral: '5m',      // Time bucket: 1m, 5m, 15m, 30m, 1h, etc.
  aggregateFunction: 'mean',   // mean | max | min | sum | count
};

export const DEFAULT_INVENTORY_CONFIG = {
  rowType: 'hosts',            // "hosts" or "items"
  categoryHandling: 'column',  // "column" or "specific"
  tableLayout: 'long_form',    // "long_form", "split_by_category", "pivot" (hosts) or "aggregated", "expanded" (items)
  scanBasis: 'latest',         // "latest"
  selectedCategories: [],      // When categoryHandling is "specific"
  pivotColumnLimit: 20,        // Max columns in pivot layout
};

export const DEFAULT_CUSTOM_QUERY_CONFIG = {
  query: '',                   // SQL query
};

/**
 * Widget configuration requirements by widget type
 * 
 * Each widget type defines:
 * - requiredFields: Fields that must be set for the widget to work (used for preview validation)
 * - optionalFields: Fields shown in UI but not strictly required
 * 
 * Field names correspond to dataSource.config properties
 */
export const WIDGET_CONFIG_REQUIREMENTS = {
  metric: {
    requiredFields: ['chartBy'],
    optionalFields: ['aggregation', 'valueColumn', 'displayName'],
  },
  pie: {
    requiredFields: ['chartBy'],
    optionalFields: ['aggregation', 'valueColumn', 'categoryLimit'],
  },
  donut: {
    requiredFields: ['chartBy'],
    optionalFields: ['aggregation', 'valueColumn', 'categoryLimit'],
  },
  bar: {
    requiredFields: ['chartBy'],
    optionalFields: ['aggregation', 'valueColumn', 'categoryLimit'],
  },
  'grouped-bar': {
    requiredFields: ['chartBy', 'groupBy'],
    optionalFields: ['aggregation', 'valueColumn', 'categoryLimit'],
  },
  'stacked-bar': {
    requiredFields: ['chartBy', 'groupBy'],
    optionalFields: ['aggregation', 'valueColumn', 'categoryLimit'],
  },
  line: {
    // For table data sources, line charts need timeColumn and yAxisField
    requiredFields: ['timeColumn', 'yAxisField'],
    optionalFields: ['groupBy'],
  },
  table: {
    requiredFields: [],
    optionalFields: ['selectedColumns'],
  },
  text: {
    requiredFields: [],
    optionalFields: ['textContent'],
  },
};

/**
 * Inventory data source configuration requirements
 */
export const INVENTORY_CONFIG_REQUIREMENTS = {
  requiredFields: ['rowType', 'tableLayout'],
  optionalFields: ['categoryHandling', 'selectedCategories', 'pivotColumnLimit', 'scanBasis'],
};

/**
 * Get the configuration requirements for a widget type
 * @param {string} widgetType - The widget type
 * @returns {object} - { requiredFields: [], optionalFields: [] }
 */
export const getWidgetConfigRequirements = (widgetType) =>
  WIDGET_CONFIG_REQUIREMENTS[widgetType] || { requiredFields: [], optionalFields: [] };

/**
 * Check if a widget type requires a specific field
 * @param {string} widgetType - The widget type
 * @param {string} field - The field name to check
 * @returns {boolean}
 */
export const widgetRequiresField = (widgetType, field) => {
  const requirements = WIDGET_CONFIG_REQUIREMENTS[widgetType];
  return requirements?.requiredFields?.includes(field) || false;
};

/**
 * Check if a widget type shows a specific field (required or optional)
 * @param {string} widgetType - The widget type
 * @param {string} field - The field name to check
 * @returns {boolean}
 */
export const widgetShowsField = (widgetType, field) => {
  const requirements = WIDGET_CONFIG_REQUIREMENTS[widgetType];
  if (!requirements) return false;
  return requirements.requiredFields?.includes(field) || requirements.optionalFields?.includes(field);
};

/**
 * Check if widget configuration is complete for preview
 * @param {string} widgetType - The widget type
 * @param {object} dsConfig - The data source config object
 * @param {boolean} hasColumnBasedSource - Whether there's a table or custom_query data source
 * @returns {boolean}
 */
export const isWidgetConfigComplete = (widgetType, dsConfig, hasColumnBasedSource = true) => {
  // Text widgets don't need data source config
  if (widgetType === 'text') return true;
  
  // If no column-based source, can't check column-based requirements
  if (!hasColumnBasedSource) return true;
  
  const requirements = WIDGET_CONFIG_REQUIREMENTS[widgetType];
  if (!requirements) return true;
  
  // Check all required fields are set
  return requirements.requiredFields.every((field) => {
    const value = dsConfig?.[field];
    return value !== undefined && value !== null && value !== '';
  });
};

/**
 * Check if inventory configuration is complete
 * @param {object} inventoryConfig - The inventory config object
 * @returns {boolean}
 */
export const isInventoryConfigComplete = (inventoryConfig) =>
  INVENTORY_CONFIG_REQUIREMENTS.requiredFields.every((field) => {
    const value = inventoryConfig?.[field];
    return value !== undefined && value !== null && value !== '';
  });

